import type { GlTransactionFormValues } from "../schemas/glTransactionForm"

import { GlTransactionRepeatByUnit } from "@/graphql/codegen/graphql"
import type { GlTransactionInsertInput, GlTransactionUpdateInput } from "@/graphql/codegen/graphql"

export const toGlTransactionCreateInput = (formValues: GlTransactionFormValues): GlTransactionInsertInput => {
  const input: GlTransactionInsertInput = {
    transactionDate: formValues.transactionDate,
    description: formValues.description,
    note: formValues.note || undefined,
    glTransactionRows: formValues.transactionLines.map((line) => ({
      type: line.type,
      glAccountId: line.glAccountId!,
      amount: line.amount!,
    })),
  }

  if (formValues.isRecurring) {
    input.repeatEndDate = formValues.repeatEndDate
    input.repeatEvery = formValues.repeatEvery
    input.repeatByUnit = formValues.repeatByUnit
  }

  return input
}

export const toGlTransactionUpdateInput = (
  formValues: GlTransactionFormValues,
  glTransactionId: string
): GlTransactionUpdateInput => ({
  glTransactionId,
  description: formValues.description,
  note: formValues.note || undefined,
  transactionDate: formValues.transactionDate,
  ...(formValues.isRecurring &&
    !formValues.isExistingRecurring && {
      repeatEndDate: formValues.repeatEndDate,
      repeatEvery: formValues.repeatEvery,
      repeatByUnit: formValues.repeatByUnit,
    }),
  glTransactionRows: formValues.transactionLines.map((line) => ({
    glTransactionRowId: line.glTransactionRowId,
    glTransactionId,
    type: line.type,
    glAccountId: line.glAccountId!,
    amount: line.amount!,
  })),
})

export type AccountingPeriodType = "past" | "current" | "future"

export interface AccountingPeriod {
  label: string
  range: string
  type: AccountingPeriodType
}

const padTwo = (n: number): string => String(n).padStart(2, "0")

const formatIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`

export const getAccountingPeriod = (transactionDate: string): AccountingPeriod | null => {
  if (!transactionDate) return null

  const date = new Date(`${transactionDate}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null

  const year = date.getFullYear()
  const month = date.getMonth()

  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)

  const label = date.toLocaleString("en-US", { month: "long", year: "numeric" })
  const range = `${formatIsoDate(firstDay)} – ${formatIsoDate(lastDay)}`

  const today = new Date()
  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth()

  let type: AccountingPeriodType
  if (year < todayYear || (year === todayYear && month < todayMonth)) {
    type = "past"
  } else if (year === todayYear && month === todayMonth) {
    type = "current"
  } else {
    type = "future"
  }

  return { label, range, type }
}

const ORDINAL_SUFFIXES = ["th", "st", "nd", "rd"]

const getOrdinalSuffix = (n: number): string => {
  const remainder100 = n % 100
  if (remainder100 >= 11 && remainder100 <= 13) return "th"
  const remainder10 = n % 10
  return ORDINAL_SUFFIXES[remainder10] ?? "th"
}

const getOrdinal = (n: number): string => `${n}${getOrdinalSuffix(n)}`

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const formatDisplayDate = (dateStr: string): string => {
  const date = new Date(`${dateStr}T00:00:00`)
  return date.toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric" })
}

export const calculateOccurrenceCount = (
  startDate: string,
  endDate: string,
  repeatEvery: number,
  repeatByUnit: GlTransactionRepeatByUnit
): number => {
  const end = new Date(`${endDate}T00:00:00`)
  const start = new Date(`${startDate}T00:00:00`)

  if (repeatByUnit === GlTransactionRepeatByUnit.Week) {
    let count = 0
    const current = new Date(start)
    while (current <= end) {
      count++
      current.setDate(current.getDate() + repeatEvery * 7)
    }
    return count
  }

  const originalDay = start.getDate()
  let count = 0

  if (repeatByUnit === GlTransactionRepeatByUnit.Month) {
    let year = start.getFullYear()
    let month = start.getMonth()
    let lastDayOfMonth = new Date(year, month + 1, 0).getDate()
    let day = Math.min(originalDay, lastDayOfMonth)
    while (new Date(year, month, day) <= end) {
      count++
      month += repeatEvery
      year += Math.floor(month / 12)
      month = month % 12
      lastDayOfMonth = new Date(year, month + 1, 0).getDate()
      day = Math.min(originalDay, lastDayOfMonth)
    }
  } else {
    // Year
    let year = start.getFullYear()
    const month = start.getMonth()
    let lastDayOfMonth = new Date(year, month + 1, 0).getDate()
    let day = Math.min(originalDay, lastDayOfMonth)
    while (new Date(year, month, day) <= end) {
      count++
      year += repeatEvery
      lastDayOfMonth = new Date(year, month + 1, 0).getDate()
      day = Math.min(originalDay, lastDayOfMonth)
    }
  }

  return count
}

export const buildRecurringSummary = (
  startDate: string,
  endDate: string,
  repeatEvery: number,
  repeatByUnit: GlTransactionRepeatByUnit
): string => {
  const start = new Date(`${startDate}T00:00:00`)
  const day = start.getDate()
  const month = MONTH_NAMES[start.getMonth()]
  const count = calculateOccurrenceCount(startDate, endDate, repeatEvery, repeatByUnit)
  const formattedEnd = formatDisplayDate(endDate)

  switch (repeatByUnit) {
    case GlTransactionRepeatByUnit.Week:
      return `Occurs every ${repeatEvery} week(s) until ${formattedEnd} (${count} occurrence${count !== 1 ? "s" : ""})`
    case GlTransactionRepeatByUnit.Month:
      return `Occurs every ${repeatEvery} month(s) on the ${getOrdinal(day)} until ${formattedEnd} (${count} occurrence${count !== 1 ? "s" : ""})`
    case GlTransactionRepeatByUnit.Year:
      if (repeatEvery === 1) {
        return `Occurs annually on ${month} ${day} until ${formattedEnd} (${count} occurrence${count !== 1 ? "s" : ""})`
      }
      return `Occurs every ${repeatEvery} year(s) on ${month} ${day} until ${formattedEnd} (${count} occurrence${count !== 1 ? "s" : ""})`
  }
}
